import { toast } from 'react-toastify'
import { useSendTransaction, useWriteContract } from 'wagmi'

import { NATIVE_TOKEN_ADDRESS } from '@src/configs/chainInfo'
import erc20Abi from '@src/contract/abi/erc20.abi.json'
import { useWaitForTransaction } from '@src/hooks/useWaitForTransaction'
import { Address, IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('useDepositTransaction')

/**
 * Hook for creating and sending deposit transactions to a specified address
 * Supports both native tokens (ETH, MATIC, etc.) and ERC20 tokens
 */
export const useDepositTransaction = () => {
    const { writeContractAsync } = useWriteContract()
    const { sendTransactionAsync } = useSendTransaction()
    const { waitForTransactionReceipt } = useWaitForTransaction()

    /**
     * Sends a deposit transaction
     * @param depositAddress Address to send tokens to
     * @param tokenAddress The token address (or native token address for ETH, etc)
     * @param amount The amount to send as a bigint
     * @param chainId The chain ID to use
     * @returns Transaction hash
     */
    const sendDepositTransaction = async (
        depositAddress: Address,
        tokenAddress: Address,
        amount: bigint,
        chainId: number
    ): Promise<string> => {
        try {
            logger.info('Creating deposit transaction', {
                depositAddress,
                tokenAddress,
                amount: amount.toString(),
                chainId,
            })

            // Handle differently based on whether it's a native token or ERC20 token
            const isNativeToken = tokenAddress === NATIVE_TOKEN_ADDRESS[chainId]

            let hash: string

            if (isNativeToken) {
                // For native tokens like ETH, use sendTransaction
                hash = await sendTransactionAsync({
                    to: depositAddress as `0x${string}`,
                    value: amount,
                    chainId,
                })
            } else {
                // For ERC20 tokens, use contract call
                hash = await writeContractAsync({
                    address: tokenAddress as `0x${string}`,
                    abi: erc20Abi,
                    functionName: 'transfer',
                    args: [depositAddress as `0x${string}`, amount],
                    chainId,
                })
            }

            logger.info('Deposit transaction sent', { hash })

            // Show success message
            toast.success('Deposit transaction sent! Please wait for confirmations.', {
                position: 'bottom-right',
                autoClose: 5000,
            })

            return hash
        } catch (error: any) {
            logger.error('Error creating deposit transaction:', error)

            // Show error message
            toast.error(`Transaction failed: ${error.message || 'Unknown error'}`, {
                position: 'bottom-right',
                autoClose: 5000,
            })

            throw error
        }
    }

    /**
     * Waits for a deposit transaction to be confirmed
     * @param hash Transaction hash to wait for
     * @param chainId The chain ID where the transaction was submitted
     */
    const waitForDeposit = async (hash: string, chainId?: number) => {
        // Pass the chainId parameter to waitForTransactionReceipt
        await waitForTransactionReceipt(hash, false, false, chainId)
    }

    return {
        sendDepositTransaction,
        waitForDeposit,
    }
}
